import { FunctionComponent } from 'react';
import { useNavigate } from 'react-router-dom';
import MedicationIcon from '@mui/icons-material/Medication';
import styles from './MedicationCard.module.css';

export interface Medication {
  id: string;
  name: string;
  genericName?: string;
  category?: string;
  priceRange?: {
    min: number;
    max: number;
  };
  averagePrice?: number;
  insuranceCoverage?: string;
  commonDosages?: string[];
  description?: string;
  brandNames?: string[];
}

export interface MedicationCardProps {
  medication: Medication;
  variant?: 'compact' | 'standard' | 'detailed';
  showGenericName?: boolean;
  showCategory?: boolean;
  showPriceRange?: boolean;
  showAveragePrice?: boolean;
  showInsuranceCoverage?: boolean;
  showDosages?: boolean;
  showDescription?: boolean;
  showBrandNames?: boolean;
  maxDosages?: number;
  onClick?: (medication: Medication) => void;
  className?: string;
}

const MedicationCard: FunctionComponent<MedicationCardProps> = ({
  medication,
  variant = 'standard',
  showGenericName = true,
  showCategory = true,
  showPriceRange = true,
  showAveragePrice = true,
  showInsuranceCoverage = true,
  showDosages = true,
  showDescription = false,
  showBrandNames = false,
  maxDosages = 3,
  onClick,
  className
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick(medication);
    } else {
      navigate(`/medication/${medication.id}`);
    }
  };

  const cardClasses = [
    styles.medicationCard,
    styles[variant],
    className
  ].filter(Boolean).join(' ');

  const displayedDosages = medication.commonDosages?.slice(0, maxDosages) || [];
  const hasMoreDosages = (medication.commonDosages?.length || 0) > maxDosages;

  return (
    <div className={cardClasses} onClick={handleClick}>
      <div className={styles.medicationInfo}>
        <div className={styles.medicationHeader}>
          <MedicationIcon className={styles.typeIcon} />
          <div className={styles.medicationDetails}>
            <h3 className={styles.medicationName}>{medication.name}</h3>
            {(showGenericName || showCategory) && (medication.genericName || medication.category) && (
              <p className={styles.medicationCategory}>
                {showGenericName && medication.genericName && medication.genericName}
                {showGenericName && medication.genericName && showCategory && medication.category && ' • '}
                {showCategory && medication.category && medication.category}
              </p>
            )}
            {showDescription && medication.description && (
              <p className={styles.medicationDescription}>{medication.description}</p>
            )}
            {showBrandNames && medication.brandNames && medication.brandNames.length > 0 && (
              <div className={styles.brandNames}>
                <span className={styles.brandLabel}>Brand names:</span>
                <span className={styles.brandList}>{medication.brandNames.join(', ')}</span>
              </div>
            )}
          </div>
        </div>
        
        {(showPriceRange || showAveragePrice) && (medication.priceRange || medication.averagePrice) && (
          <div className={styles.priceInfo}>
            {showPriceRange && medication.priceRange && (
              <div className={styles.priceRange}>
                <span className={styles.priceLabel}>Price Range</span>
                <span className={styles.priceValue}>
                  ${medication.priceRange.min} - ${medication.priceRange.max}
                </span>
              </div>
            )}
            {showAveragePrice && medication.averagePrice && (
              <div className={styles.averagePrice}>
                <span className={styles.priceLabel}>Average</span>
                <span className={styles.priceValue}>${medication.averagePrice}</span>
              </div>
            )}
          </div>
        )}
        
        {showDosages && displayedDosages.length > 0 && (
          <div className={styles.dosageInfo}>
            <span className={styles.dosageLabel}>Common dosages:</span>
            <div className={styles.dosageList}>
              {displayedDosages.map((dosage: string, index: number) => (
                <span key={index} className={styles.dosageChip}>{dosage}</span>
              ))}
              {hasMoreDosages && (
                <span className={styles.dosageChip}>+{(medication.commonDosages?.length || 0) - maxDosages} more</span>
              )}
            </div>
          </div>
        )}
        
        {showInsuranceCoverage && medication.insuranceCoverage && (
          <div className={styles.medicationFooter}>
            <span className={styles.insuranceCoverage}>
              Insurance covers {medication.insuranceCoverage}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default MedicationCard;